import { useRef } from "react";
import { useToast } from "@/context/ToastContext";

/**
 * A subscription URL with a copy button.
 *
 * Deliberately an <input>, not an <a>: this URL is a credential, and following
 * it in a browser downloads the .ics file rather than subscribing to anything.
 * The only useful action is copying it, so that is the only action offered.
 *
 * Clipboard access can be refused (permissions, an insecure origin, an embedded
 * webview), so a failed copy falls back to selecting the text and saying so —
 * the user can still finish the job by hand.
 */
export function CopyableUrl({ url }: { url: string }) {
  const toast = useToast();
  const ref = useRef<HTMLInputElement>(null);

  async function copy() {
    ref.current?.select();
    try {
      await navigator.clipboard.writeText(url);
      toast("Calendar link copied");
    } catch {
      toast("Select the link and press ⌘C");
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        ref={ref}
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        onClick={(e) => e.currentTarget.select()}
        aria-label="Calendar subscription link"
        className="mono"
        style={{
          flex: 1, minWidth: 0, padding: "7px 10px", borderRadius: 7,
          border: "1px solid var(--line2)", background: "var(--tint)",
          color: "var(--ink3)", fontSize: 11.5,
        }}
      />
      <button
        onClick={copy}
        style={{
          flex: "none", padding: "7px 14px", borderRadius: 999,
          border: "1px solid var(--line2)", background: "var(--surface)",
          color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
        }}
      >
        Copy
      </button>
    </div>
  );
}

/** Where to paste it. The same two steps on every screen that shows a link. */
export function SubscribeHint() {
  return (
    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8, lineHeight: 1.5 }}>
      iPhone: Settings › Calendar › Accounts › Add Account › Other › Add Subscribed Calendar.
      Google Calendar: Other calendars › From URL — it refreshes on Google's own
      schedule, often hours behind.
      <br />
      Anyone with the link can read these dates, so treat it like a password.
    </div>
  );
}
